import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  EyeIcon, 
  PencilIcon,
  CalendarIcon,
  TrophyIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import VisionCreationModal from './VisionCreationModal';
import ArchiveVisionModal from './ArchiveVisionModal';
import DeleteVisionModal from './DeleteVisionModal';
import HeaderNav from './HeaderNav';
import VisionImagePlate from './common/VisionImagePlate';
import AdvisorMatch from './AdvisorMatch';
import { usePendingVisionImagePoll } from '../hooks/usePendingVisionImagePoll';
import VisionRiskWarningCard from './VisionRiskWarningCard';
import { useUpgrade } from '../context/UpgradeContext';
import VisionQuotaIndicator from './upgrade/VisionQuotaIndicator';
import VisionLimitCTA from './upgrade/VisionLimitCTA';
import { useCurrency } from '../context/CurrencyContext';


/* ---------- Axios bootstrap (safe to include multiple times) ---------- */
(function ensureAxiosConfig(){
  // Prefer explicitly provided baseURL (e.g., window.__API_BASE__), else default to same-origin
  const hinted = typeof window !== 'undefined' ? (window.__API_BASE__ || '') : '';
  if (!axios.defaults.baseURL && hinted) axios.defaults.baseURL = hinted;

  // Add Authorization header from localStorage token if present
  if (!axios.defaults.headers.common['Authorization']) {
    try {
      const tok = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('access_token');
      if (tok) axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    } catch {}
  }

  // Basic error logging interceptor (non-invasive)
  if (!axios.__visionsInterceptor) {
    axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        return Promise.reject(err);
      }
    );
    axios.__visionsInterceptor = true;
  }
})();

/* -------------------------- Helpers -------------------------- */
const pctProgress = (current, target) => {
  const c = Number(current) || 0;
  const t = Number(target) || 0;
  if (t <= 0) return 0;
  const p = (c / t) * 100;
  return Math.max(0, Math.min(100, p));
};

const safeDate = (d) => {
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString();
  } catch {
    return '';
  }
};

const VisionsPage = ({ user, onLogout, initialVisions = [], onVisionsUpdate }) => {
  const { permissions, openUpgrade, refreshPermissions } = useUpgrade();
  const { formatAmount } = useCurrency();
  const atVisionLimit = Boolean(permissions?.at_vision_limit);

  const tryOpenVisionModal = () => {
    if (atVisionLimit) {
      openUpgrade('vision_limit');
      return;
    }
    setShowVisionModal(true);
  };
  const navigate = useNavigate();

  const [visions, setVisions] = useState(initialVisions);
  const [archivedVisions, setArchivedVisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  const [showVisionModal, setShowVisionModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [accountState, setAccountState] = useState(null);

  // Prevent duplicate initial loads (StrictMode/double-mount, parent re-renders, etc.)
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    return () => console.log('[VisionsPage] UNMOUNT');
  }, []);

  /* Keep visions in sync if prop arrives with data (e.g., SSR or parent prefetch) */
  useEffect(() => {
    if (Array.isArray(initialVisions) && initialVisions.length > 0) {
      setVisions(initialVisions);
    }
  }, [initialVisions]);

  /* Loaders */
  const loadVisions = useCallback(async () => {
    try {
      let res;
      try {
        res = await axios.get('/api/visions');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          // attempt demo if unauth, but keep UI functional
          res = await axios.get('/api/visions/demo');
        } else {
          throw err;
        }
      }
      const data = res.data;
      setVisions(Array.isArray(data) ? data : (data?.visions || []));
      // IMPORTANT: Do NOT call onVisionsUpdate() here — it can cause re-render loops
    } catch (err) {
      setVisions([]);
    }
  }, []); // stable identity

  const loadArchivedVisions = useCallback(async () => {
    try {
      let res;
      try {
        res = await axios.get('/api/visions/archived');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          res = await axios.get('/api/visions/archived/demo');
        } else {
          throw err;
        }
      }
      setArchivedVisions(res.data?.visions || []);
    } catch (err) {
      setArchivedVisions([]);
    }
  }, []); // stable identity

  /* Load data ONCE on mount (prevents infinite loop) */
  useEffect(() => {
    if (loadedOnceRef.current) return;   // ← key guard
    loadedOnceRef.current = true;

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadVisions(), loadArchivedVisions()]);
        try {
          const stateRes = await axios.get('/api/accounts/state');
          if (!cancelled) setAccountState(stateRes.data);
        } catch {
          if (!cancelled) setAccountState(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadVisions, loadArchivedVisions]);

  /* Safety loading timeout (prevents infinite spinners if a call hangs) */
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => setLoading(false), 12000);
    return () => clearTimeout(t);
  }, [loading]);

  const handleVisionCreated = async () => {
    await Promise.all([loadVisions(), refreshPermissions()]);
    setShowVisionModal(false);
    onVisionsUpdate && onVisionsUpdate();
  };

  usePendingVisionImagePoll(visions, setVisions);

  const handleVisionClick = (vision) => {
    navigate(`/vision/${vision.vision_id}`);
  };

  const openArchiveModal = (vision) => {
    setArchiveTarget(vision);
    setShowArchiveModal(true);
  };

  const confirmArchive = async ({ note, photoUrl }) => {
    if (!archiveTarget) return;
    try {
      await axios.post('/api/visions/achieve', {
        vision_id: archiveTarget.vision_id,
        note: note || '',
        photo_url: photoUrl || null,
      });
      setShowArchiveModal(false);
      setArchiveTarget(null);
      await Promise.all([loadVisions(), loadArchivedVisions(), refreshPermissions()]);
      setActiveTab('achieved');
    } catch (error) {
    }
  };

  const openDeleteModal = (vision) => {
    setDeleteTarget(vision);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (vision) => {
    if (!vision) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/api/visions/${vision.vision_id}`);
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await Promise.all([loadVisions(), loadArchivedVisions(), refreshPermissions()]);
    } catch (error) {
      window?.alert?.('Failed to delete vision. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const VisionCard = ({ vision, isArchived = false }) => {
    const progress = pctProgress(vision.current_amount, vision.target_amount);
    const isAchieved = progress >= 100;

    return (
      <motion.article
        className="surface-panel overflow-hidden cursor-pointer transition-colors duration-300 hover:bg-pine-tint"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        onClick={() => handleVisionClick(vision)}
      >
        <VisionImagePlate
          src={vision.image_url}
          alt={vision.title}
          className="m-0 rounded-t-lg rounded-b-none border-0 border-b border-hairline"
        />
        <div className="p-5">
          <div className="flex justify-between gap-2 mb-2">
            <h3 className="font-display text-h3 text-ink line-clamp-2">{vision.title}</h3>
            <span className="text-label text-ink-muted shrink-0">
              {isArchived ? 'Achieved' : isAchieved ? 'Ready' : 'In progress'}
            </span>
          </div>
          {vision.description && (
            <p className="text-body text-ink-muted mb-4 line-clamp-2">{vision.description}</p>
          )}
          {!isArchived && (
            <>
              <div className="flex justify-between text-label text-ink-muted mb-1.5">
                <span>Progress</span>
                <span className="figure text-ink">{Math.round(progress)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-hairline overflow-hidden mb-2">
                <div className="h-full bg-pine rounded-full" style={{ width: `${progress}%` }} />
              </div>
              <div className="flex justify-between text-label text-ink-muted figure mb-4">
                <span>{formatAmount(vision.current_amount)}</span>
                <span>{formatAmount(vision.target_amount)}</span>
              </div>
            </>
          )}
          <div className="flex items-center justify-between pt-3 border-t border-hairline">
            <span className="text-label text-ink-faint flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              {safeDate(vision.created_at) || '—'}
            </span>
            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
              <button type="button" onClick={() => handleVisionClick(vision)} className="btn-ghost p-2" title="Open">
                <PencilIcon className="w-4 h-4" />
              </button>
              {!isArchived && isAchieved && (
                <button type="button" onClick={() => openArchiveModal(vision)} className="btn-ghost p-2" title="Archive">
                  <ArchiveBoxIcon className="w-4 h-4" />
                </button>
              )}
              <button type="button" onClick={() => openDeleteModal(vision)} className="btn-ghost p-2 text-danger" title="Delete">
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.article>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-2 border-hairline border-t-pine rounded-full animate-spin" />
        <p className="text-body text-ink-muted">Loading visions…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      {user && (
        <HeaderNav
          activeSection="visions"
          onSectionChange={(id) => id === 'dashboard' && navigate('/dashboard')}
          user={user}
          onLogout={onLogout}
          visionCount={visions.length}
        />
      )}

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-8">
        <div className="mb-8">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-ink-muted hover:text-ink mb-6 text-body transition-colors duration-300"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to dashboard
          </button>
          <div className="flex flex-wrap items-end justify-between gap-4 pb-6 border-b border-hairline">
            <div>
              <h1 className="font-display text-h1 text-ink mb-2">Your visions</h1>
              <p className="text-body text-ink-muted mb-2">Plan, track, and achieve your financial goals</p>
              <VisionQuotaIndicator permissions={permissions} />
            </div>
            {atVisionLimit ? (
              <VisionLimitCTA onUpgrade={() => openUpgrade('vision_limit')} compact />
            ) : (
              <button type="button" onClick={tryOpenVisionModal} className="btn-primary flex items-center gap-2">
                <PlusIcon className="w-5 h-5" />
                Create vision
              </button>
            )}
          </div>
        </div>

        <VisionRiskWarningCard show={accountState?.vision_at_risk === true} />

        <div className="flex gap-6 mb-8 border-b border-hairline">
          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`pb-3 text-body font-medium transition-colors duration-300 ${
              activeTab === 'active' ? 'nav-tab-active' : 'nav-tab'
            }`}
          >
            Active ({visions.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('achieved')}
            className={`pb-3 text-body font-medium transition-colors duration-300 ${
              activeTab === 'achieved' ? 'nav-tab-active' : 'nav-tab'
            }`}
          >
            Achieved ({archivedVisions.length})
          </button>
        </div>

        {/* Visions Grid */}
        <div>
          <AnimatePresence mode="wait">
            {activeTab === 'active' ? (
              <motion.div
                key="active"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {visions.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <EyeIcon className="w-12 h-12 text-ink-faint mx-auto mb-4" />
                    <h3 className="font-display text-h2 text-ink mb-2">No active visions</h3>
                    <p className="text-body text-ink-muted mb-6">Create your first vision to start planning</p>
                    {atVisionLimit ? (
                      <div className="max-w-md mx-auto text-left">
                        <VisionLimitCTA onUpgrade={() => openUpgrade('vision_limit')} />
                      </div>
                    ) : (
                      <button type="button" onClick={tryOpenVisionModal} className="btn-primary">
                        Create your first vision
                      </button>
                    )}
                  </div>
                ) : (
                  visions.map((vision) => <VisionCard key={vision.vision_id} vision={vision} />)
                )}
              </motion.div>
            ) : (
              <motion.div
                key="achieved"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 6 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {archivedVisions.length === 0 ? (
                  <div className="col-span-full text-center py-16">
                    <TrophyIcon className="w-12 h-12 text-ink-faint mx-auto mb-4" />
                    <h3 className="font-display text-h2 text-ink mb-2">No achieved visions yet</h3>
                    <p className="text-body text-ink-muted">Complete active visions to see them here</p>
                  </div>
                ) : (
                  archivedVisions.map((vision) => (
                    <VisionCard key={vision.vision_id} vision={vision} isArchived />
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {activeTab === 'active' && visions.length > 0 && <AdvisorMatch user={user} />}
      </div>

      {/* Modals */}
      {showVisionModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)' }}>
          <VisionCreationModal
            onClose={() => setShowVisionModal(false)}
            onVisionCreated={handleVisionCreated}
          />
        </div>
      )}

      {showArchiveModal && archiveTarget && (
        <ArchiveVisionModal
          vision={archiveTarget}
          onClose={() => setShowArchiveModal(false)}
          onConfirm={confirmArchive}
        />
      )}

      {showDeleteModal && deleteTarget && (
        <DeleteVisionModal
          vision={deleteTarget}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default VisionsPage;
